import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { setDisplayLocale } from "../time/format";
import enAdmin from "./locales/en/admin.json";
import enCommon from "./locales/en/common.json";
import ruAdmin from "./locales/ru/admin.json";
import ruCommon from "./locales/ru/common.json";

export const LANGUAGES = ["en", "ru"] as const;
export type Lang = (typeof LANGUAGES)[number];

/** Maps the UI language onto the locale `Intl` formats dates and numbers in. */
const DISPLAY_LOCALE: Record<Lang, string> = { en: "en-GB", ru: "ru-RU" };

function detectLanguage(): Lang {
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem("jtarchive.lang");
  } catch {
    // Storage can be unavailable; browser languages still apply.
  }
  const browser =
    typeof navigator === "undefined" ? [] : [...navigator.languages, navigator.language];
  for (const candidate of [stored, ...browser]) {
    const lang = candidate?.toLowerCase().split("-")[0];
    if (lang === "en" || lang === "ru") return lang;
  }
  return "en";
}

void i18n.use(initReactI18next).init({
  resources: {
    en: { common: enCommon, admin: enAdmin },
    ru: { common: ruCommon, admin: ruAdmin },
  },
  supportedLngs: LANGUAGES,
  lng: detectLanguage(),
  fallbackLng: "en",
  // "ru-RU" from navigator resolves to the "ru" catalogue rather than missing it.
  load: "languageOnly",
  ns: ["common", "admin"],
  defaultNS: "common",
  // React escapes for us; i18next escaping again turns an apostrophe into &#39;.
  interpolation: { escapeValue: false },
});

function applyLanguage(lng: string): void {
  const lang = (LANGUAGES as readonly string[]).includes(lng) ? (lng as Lang) : "en";
  // Guarded rather than assumed. `api/client.ts` imports this module to put the active
  // language on Accept-Language, which drags it into every node-environment test that
  // reaches the API client -- and those have no document. The locale below still applies.
  if (typeof document !== "undefined") document.documentElement.lang = lang;
  setDisplayLocale(DISPLAY_LOCALE[lang]);
  try {
    window.localStorage.setItem("jtarchive.lang", lang);
  } catch {
    // Language changes must work without browser storage.
  }
}

applyLanguage(i18n.resolvedLanguage ?? "en");
i18n.on("languageChanged", applyLanguage);

export default i18n;
